/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import * as R from "ramda";

import Icon, { CircularIcon } from "components/Icon";
import Text from "components/Text";
import { ButtonDiv } from "components/accessibility/Div";
import SummaryTable from "components/projects/SummaryTable";
import { useAnswerContext } from "contexts/answerContext";
import useModalToggle from "hooks/useModalToggle";
import { useRequirementsByID } from "queries/requirements";
import { formatMoney } from "utils/format";
import { compact } from "utils/func";

import styles from "./Peekbar.scss";

const PermissionBar = ({ requirements, showFees, activate }) => {
  let feesTotal = 0;
  const { record } = useAnswerContext();
  const feeIDs = R.chain(R.propOr([], "fee_ids"), requirements);

  if (!R.isEmpty(feeIDs)) {
    const fees = compact(R.props(feeIDs, record.calculated_fee_totals));
    feesTotal = R.sum(fees);
  }

  return (
    <ButtonDiv className={styles.container} onClick={activate} role="button" tabIndex="0">
      <div className={styles.open}>
        <Icon icon="angle-up" />
      </div>

      <div className={styles.group}>
        <div className={styles.requirementCount}>
          <Icon size="lg" icon="file-alt" />
          <span>
            <Text t="guides.peekbar.requirement_count" requirements={requirements.length} />
          </span>
        </div>
        {showFees && (
          <div className={styles.fees}>
            <Icon size="lg" icon="usd-circle" />
            <span>{formatMoney(feesTotal)}</span>
          </div>
        )}
      </div>
    </ButtonDiv>
  );
};

PermissionBar.propTypes = {
  requirements: PropTypes.array.isRequired,
  showFees: PropTypes.bool,
  activate: PropTypes.func.isRequired,
};

const PeekBar = ({ requirementID, showFees }) => {
  const { active, activate, deactivate } = useModalToggle();
  const { record } = useAnswerContext();
  const requirementIDs = requirementID != null ? [requirementID] : record.requirement_ids;
  const { data: requirements = [] } = useRequirementsByID(requirementIDs);

  return (
    <div
      onClick={() => !active && activate()}
      className={classnames({ [styles.active]: active })}
      aria-hidden="true"
    >
      <PermissionBar activate={activate} requirements={requirements} showFees={showFees} />
      <div className={styles.window}>
        <a className={styles.close} onClick={deactivate}>
          ✕
        </a>
        <div className={styles.header}>
          <div className={styles.calculator}>
            <CircularIcon icon="calculator" />
          </div>
          <h1>
            <Text t="guides.peekbar.header" />
          </h1>
          {requirements.length > 0 || (
            <p>
              <Text t="guides.peekbar.instructions" />
            </p>
          )}
        </div>
        <SummaryTable showFees={showFees} isSidebar isAriaHidden requirementID={requirementID} />
      </div>
    </div>
  );
};

export const PeekBarWithFees = (props) => <PeekBar {...props} showFees />;

export default PeekBar;
